//const API_BASE = '/api/audit';
const API_BASE = `${import.meta.env.VITE_API_BASE_URL}/audit`

export const fetchAuditTrail = async ({
  page = 1,
  pageSize = 25,
  userFilter = null,
  codeFilter = null,
  fromDate = null,
  toDate = null
} = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });

  if (userFilter) params.append('userFilter', userFilter);
  if (codeFilter) params.append('codeFilter', codeFilter);
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audit trail: ${response.status}`);
  }

  return response.json();
};

export const fetchAuditCodes = async () => {
  const response = await fetch(`${API_BASE}/codes`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audit codes: ${response.status}`);
  }

  return response.json();
};

export const logAuditEvent = async ({ auditCode, annotation = null, entityId = null, entityType = null }) => {
  try {
    const response = await fetch(`${API_BASE}/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ auditCode, annotation, entityId, entityType })
    });

    if (!response.ok) {
      console.warn(`Audit logging failed: ${response.status}`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('Audit logging error:', error);
    return false; // Silent fail for audit logging
  }
};

// ============ CENTRALIZED AUDIT FUNCTIONS ============

// Pre-defined audit codes for consistency
export const AUDIT_CODES = {
  // Customer Settings & Contractors
  CONTRACTOR_ADD_CLICKED: 'AUD020',
  CONTRACTOR_CREATED: 'AUD021',
  CONTRACTOR_UPDATED: 'AUD022',
  CONTRACTOR_DELETED: 'AUD023',
  SETTINGS_VIEWED: 'AUD024',
  SETTINGS_UPDATED: 'AUD025',

  // UI Actions
  BUTTON_CLICKED: 'AUD030',
  FORM_OPENED: 'AUD031',
  MODAL_OPENED: 'AUD032'
};

// Centralized logging functions
export const auditLog = {
  // Button clicks
  async buttonClick(buttonName, context = null, additionalInfo = null) {
    await logAuditEvent({
      auditCode: AUDIT_CODES.BUTTON_CLICKED,
      annotation: `Button clicked: ${buttonName}${context ? ` in ${context}` : ''}${additionalInfo ? ` - ${additionalInfo}` : ''}`,
      entityType: 'ui_action'
    });
  },

  // Contractor specific actions
  async contractorAddClicked(context = 'Customer Settings') {
    await logAuditEvent({
      auditCode: AUDIT_CODES.CONTRACTOR_ADD_CLICKED,
      annotation: `Add Contractor button clicked in ${context}`,
      entityType: 'contractor'
    });
  },

  async contractorCreated(contractorId = null, contractorName = null) {
    await logAuditEvent({
      auditCode: AUDIT_CODES.CONTRACTOR_CREATED,
      annotation: `Contractor created${contractorName ? `: ${contractorName}` : ''}`,
      entityId: contractorId?.toString(),
      entityType: 'contractor'
    });
  },

  // Settings actions
  async settingsViewed(settingsType = 'Customer Settings') {
    await logAuditEvent({
      auditCode: AUDIT_CODES.SETTINGS_VIEWED,
      annotation: `${settingsType} page viewed`,
      entityType: 'settings'
    });
  },

  // Form/Modal actions
  async modalOpened(modalName, context = null) {
    await logAuditEvent({
      auditCode: AUDIT_CODES.MODAL_OPENED,
      annotation: `Modal opened: ${modalName}${context ? ` from ${context}` : ''}`,
      entityType: 'ui_action'
    });
  },

  // Generic custom logging
  async custom(auditCode, annotation, entityType = null, entityId = null) {
    await logAuditEvent({
      auditCode,
      annotation,
      entityType,
      entityId: entityId?.toString()
    });
  }
};
